import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  alpha,
  useTheme,
  IconButton,
} from '@mui/material';
import {
  Add,
  Warehouse,
  LocalShipping,
  Store,
  Edit,
  Delete,
  CheckCircle,
  Warning,
  Build,
} from '@mui/icons-material';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';
import { simpleApi, Warehouse as WarehouseType, WarehouseCreate } from '../../services/simpleApi';

const WarehousesPage: React.FC = () => {
  const theme = useTheme();
  const [warehouses, setWarehouses] = useState<WarehouseType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<WarehouseType | null>(null);
  const [formData, setFormData] = useState<WarehouseCreate>({
    name: '',
    warehouse_type: 'warehouse',
    total_capacity: 10000,
  });

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const response = await simpleApi.warehouses.list();
      setWarehouses(response.items);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const handleOpenDialog = (warehouse?: WarehouseType) => {
    if (warehouse) {
      setEditingWarehouse(warehouse);
      setFormData({
        name: warehouse.name,
        warehouse_id: warehouse.warehouse_id,
        warehouse_type: warehouse.warehouse_type,
        address: warehouse.address,
        city: warehouse.city,
        country: warehouse.country,
        postal_code: warehouse.postal_code,
        latitude: warehouse.latitude,
        longitude: warehouse.longitude,
        total_capacity: warehouse.total_capacity,
        manager_name: warehouse.manager_name,
        contact_email: warehouse.contact_email,
        contact_phone: warehouse.contact_phone,
        operating_hours: warehouse.operating_hours,
      });
    } else {
      setEditingWarehouse(null);
      setFormData({
        name: '',
        warehouse_type: 'warehouse',
        total_capacity: 10000,
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingWarehouse) {
        await simpleApi.warehouses.update(editingWarehouse.id, formData);
        setSuccess('Warehouse updated successfully');
      } else {
        await simpleApi.warehouses.create(formData);
        setSuccess('Warehouse created successfully');
      }
      setOpenDialog(false);
      fetchWarehouses();
    } catch (err: any) {
      setError(err.message || 'Failed to save warehouse');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this warehouse?')) return;
    try {
      await simpleApi.warehouses.delete(id);
      setSuccess('Warehouse deleted successfully');
      fetchWarehouses();
    } catch (err: any) {
      setError(err.message || 'Failed to delete warehouse');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'maintenance': return 'warning';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle />;
      case 'maintenance': return <Build />;
      default: return <Warning />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'warehouse': return <Warehouse />;
      case 'distribution_center': return <LocalShipping />;
      case 'retail': return <Store />;
      default: return <Warehouse />;
    }
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization >= 0.9) return theme.palette.error.main;
    if (utilization >= 0.75) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const totalCapacity = warehouses.reduce((sum, w) => sum + w.total_capacity, 0);
  const avgUtilization = warehouses.length > 0
    ? warehouses.reduce((sum, w) => sum + w.current_utilization, 0) / warehouses.length
    : 0;
  const activeCount = warehouses.filter(w => w.status === 'active').length;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Warehouses
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your warehouses and distribution centers
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
          Add Warehouse
        </Button>
      </Box>

      {error && <ErrorAlert error={error} onRetry={fetchWarehouses} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {warehouses.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Warehouses
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                {activeCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                {totalCapacity.toLocaleString()} m²
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Capacity
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                {(avgUtilization * 100).toFixed(0)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Utilization
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Warehouses Grid */}
      {loading ? (
        <LoadingState message="Loading warehouses..." />
      ) : warehouses.length === 0 ? (
        <EmptyState
          title="No warehouses found"
          description="Add your first warehouse to start managing inventory locations"
          actionLabel="Add Warehouse"
          onAction={() => handleOpenDialog()}
          icon={<Warehouse sx={{ fontSize: 64, color: 'text.disabled' }} />}
        />
      ) : (
        <Grid container spacing={3}>
          {warehouses.map((warehouse) => {
            const utilizationPercentage = warehouse.current_utilization * 100;
            const utilizationColor = getUtilizationColor(warehouse.current_utilization);
            
            return (
              <Grid item xs={12} md={6} key={warehouse.id}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                    },
                  }}
                >
                  <Box sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          {getTypeIcon(warehouse.warehouse_type)}
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {warehouse.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {warehouse.warehouse_id} • {warehouse.warehouse_type.replace('_', ' ').toUpperCase()}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" onClick={() => handleOpenDialog(warehouse)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(warehouse.id)}>
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Status */}
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={getStatusIcon(warehouse.status)}
                        label={warehouse.status.toUpperCase()}
                        size="small"
                        color={getStatusColor(warehouse.status) as any}
                      />
                    </Box>

                    {/* Address */}
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {warehouse.address || 'No address specified'}<br />
                      {warehouse.city && `${warehouse.city}, `}{warehouse.country || ''}
                    </Typography>

                    {/* Metrics */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Capacity
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {warehouse.total_capacity.toLocaleString()} m²
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Operating Hours
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {warehouse.operating_hours || '24/7'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Utilization */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Utilization
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {utilizationPercentage.toFixed(0)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={utilizationPercentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: alpha(utilizationColor, 0.1),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: utilizationColor,
                          },
                        }}
                      />
                    </Box>

                    {/* Manager Info */}
                    <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        Manager
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {warehouse.manager_name || 'Not Assigned'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {warehouse.contact_email || warehouse.contact_phone || ''}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <SuccessSnackbar
        open={!!success}
        message={success || ''}
        onClose={() => setSuccess(null)}
      />

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingWarehouse ? 'Edit Warehouse' : 'Add New Warehouse'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Warehouse Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={formData.warehouse_type}
                  onChange={(e) => setFormData({ ...formData, warehouse_type: e.target.value })}
                >
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                  <MenuItem value="distribution_center">Distribution Center</MenuItem>
                  <MenuItem value="retail">Retail Location</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address || ''}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country || ''}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.postal_code || ''}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacity (m²)"
                type="number"
                value={formData.total_capacity}
                onChange={(e) => setFormData({ ...formData, total_capacity: parseInt(e.target.value) || 0 })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Operating Hours"
                value={formData.operating_hours || ''}
                onChange={(e) => setFormData({ ...formData, operating_hours: e.target.value })}
                placeholder="e.g., Mon-Fri 8am-6pm"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manager Name"
                value={formData.manager_name || ''}
                onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contact_email || ''}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.contact_phone || ''}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={formData.latitude || ''}
                onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || undefined })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={formData.longitude || ''}
                onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || undefined })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingWarehouse ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarehousesPage;
